import React, {useCallback, useEffect, useMemo} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Alert, Button, Tabs} from '@forecast-it/design-system';
import Util from '../../../../forecast-app/shared/util/util';
import {closeModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import CreateNovaAgentMutation from '../../../../mutations/nova/CreateNovaAgentMutation';
import DuplicateNovaAgentMutation from '../../../../mutations/nova/DuplicateNovaAgentMutation';
import CreateNovaAgentCategoryMutation from '../../../../mutations/nova/CreateNovaAgentCategoryMutation';
import UpdateNovaAgentMutation from '../../../../mutations/nova/UpdateNovaAgentMutation';
import {AgentFormValues} from '../shared/novaAgentForm';
import {toRelayGlobalId} from '../shared/relayGlobalId';
import {showCreateAgentModal} from '../modals/showCreateAgentModal';
import {showNovaVersionHistoryModal} from '../modals/showNovaVersionHistoryModal';
import {showDeleteNovaCategoryModal} from '../modals/showDeleteNovaCategoryModal';
import {showDeleteNovaAgentModal} from '../modals/showDeleteNovaAgentModal';
import {NovaAgentVersionEntry} from '../modals/NovaVersionHistoryModal';
import {NovaAgentDraft} from '../state/useNovaAgentDraft';
import {NovaVariable} from '../shared/novaVariableLibrary';
import {useWarnOnUnsavedChanges} from '../state/useWarnOnUnsavedChanges';
import {SaveState, isSaveDisabled} from '../shared/saveState';
import {NovaStudioColor} from '../shared/novaStudioColors';
import {NovaModel, isNovaModelUnavailable} from '../shared/novaModelList';
import {isRunnable} from '../shared/novaInputValidation';
import {CATEGORY_NAME_TAKEN, categoryNameTakenError} from '../shared/categoryName';
import {NovaDeployTab} from '../deploy/NovaDeployTab';
import {AudienceTargets, DeploymentSource} from '../deploy/novaDeploymentData';
import {DeployDirectory} from '../deploy/novaDeployDirectory';
import {NovaStudioHeader} from './NovaStudioHeader';
import {DeleteCategoryPayload, NovaStudioCatalogue} from './NovaStudioCatalogue';
import {NovaPromptTab} from './NovaPromptTab';
import {NovaWorkflowPromptTab} from './NovaWorkflowPromptTab';
import {NovaReferenceFile} from './NovaReferenceFiles';
import {NovaAgentMdTab} from './NovaAgentMdTab';
import {NovaWorkflowAgentMdTab} from './NovaWorkflowAgentMdTab';
import {StudioWorkflowVariableGroup} from './NovaWorkflowVariableLibrary';
import {TASK_AGENT_VARIANT, WORKFLOW_VARIANT} from './novaStudioVariant';
import {
	StudioAgentEntry,
	StudioCategoryEntry,
	StudioTab,
	StudioWorkflowEntry,
	studioAgentDisplayName,
	useNovaStudioShell,
} from './useNovaStudioShell';

const Shell = styled.div`
	display: flex;
	flex-direction: row;
	flex-grow: 1;
	min-height: 0;
`;
const MainColumn = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-width: 0;
	min-height: 0;
`;
// The page column is height-bounded (see StudioPageColumn in NovaStudioPage.tsx), so the tab content
// — not the page — has to absorb its own overflow: the Prompt tab alone stacks title, description,
// category chips and two tall editors, which is taller than a laptop viewport. Without this the
// bottom of the User-prompt editor would be unreachable.
// A workflow's tab content: the save error, when there is one, stacked above the tab. It takes the width
// it is given and never asks for more: without the containment, opening a library full of long
// descriptions widens the main column's preferred size and squeezes the catalogue beside it.
const ContentColumn = styled.div`
	contain: inline-size;
	display: flex;
	flex-direction: column;
	gap: 20px;
`;
// A workflow's failed save is reported at the top of the content, with Retry beside the message.
const SaveErrorRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;
const SaveErrorMessage = styled.div`
	flex: 1;
	min-width: 0;
`;
const TabContent = styled.div<{$padding: string}>`
	flex-grow: 1;
	min-height: 0;
	overflow-y: auto;
	padding: ${({$padding}) => $padding};
`;
// The tab strip gets breathing room around it so Prompt / agent.md / Deploy aren't crowded against
// the header and the content below.
const TabBar = styled.div<{$padding: string}>`
	padding: ${({$padding}) => $padding};
`;
// Shown in the main column while nothing is selected: a single centred prompt, no form.
const EmptyState = styled.div`
	display: flex;
	flex-grow: 1;
	align-items: center;
	justify-content: center;
	color: ${NovaStudioColor.textMuted};
	font-size: 16px;
`;

// The message id for each tab's label, read via formatMessage in the component body below — a
// Record here is only a lookup table, not itself user-facing copy.
const TAB_LABEL_IDS: Record<StudioTab, string> = {
	[StudioTab.Prompt]: 'nova.studio.tab.prompt',
	[StudioTab.AgentMd]: 'nova.studio.tab.agentMd',
	[StudioTab.Deploy]: 'nova.studio.tab.deploy',
};
const TAB_ORDER: StudioTab[] = [StudioTab.Prompt, StudioTab.AgentMd, StudioTab.Deploy];

interface Props {
	categories: StudioCategoryEntry[];
	agents: StudioAgentEntry[];
	// The declared agentic workflows, listed on the library's Workflows tab. Each opens the Nova
	// agent record with the same agentId.
	workflows?: StudioWorkflowEntry[];
	// The combined working draft (prompt inputs + category) for the selected agent — owned by
	// NovaStudioPage, which also runs the on-demand fetch that seeds it. The shell only threads it
	// through to the Prompt tab and reads its live save-state for the header.
	draft: NovaAgentDraft;
	// The fetched variable library — owned by NovaStudioPage (the page query's
	// `viewer.novaVariableLibrary`) and threaded through only to feed the Prompt tab's variable
	// library panel.
	variables: NovaVariable[];
	// The removed variables — owned by NovaStudioPage alongside `variables`, and threaded through
	// only so each tab can name a reference to one as broken rather than as a name that never
	// existed. Never offered for insertion.
	removedVariables: NovaVariable[];
	models: NovaModel[];
	defaultModelId: string | null;
	// The selected agent's version list — fetched alongside the prompt content by the same
	// on-demand detail query (see NovaStudioPage.tsx) and threaded through only to feed the
	// version-history modal opened below. Which entry is current comes from each version's own
	// `isCurrent` flag.
	versions: NovaAgentVersionEntry[];
	// Restores a prior version (mutation + reloading the restored content into the draft) —
	// owned by NovaStudioPage.tsx, which is also what re-runs the detail fetch afterwards.
	onRestoreVersion: (versionNumber: number) => Promise<void>;
	// Whether the selected agent's real content has actually finished loading into the draft —
	// owned by NovaStudioPage.tsx (see its detail-load gate, `isDetailLoadedForSelected`), since
	// only it knows whether the on-demand detail fetch has landed for the current selection.
	// Threaded into both Save triggers (the header button and the ⌘S/Ctrl+S shortcut below) via
	// `isSaveDisabled` so neither can ever persist the draft's empty/stale placeholder values over
	// the agent's real prompt bodies. Defaults to true so callers that don't exercise this gate
	// (e.g. this suite's own tests) keep the old dirty/in-flight-only behavior. Narrowed further by
	// `canSaveDraft` below, which also refuses a save the service is certain to reject.
	canSave?: boolean;
	// Fired whenever the catalogue selection changes, so the page can fetch that agent's content
	// on demand and load it into the draft. `selectedAgentId` itself stays owned by
	// `useNovaStudioShell` (it also drives which tab is active), so this is the only way out.
	onSelectedAgentIdChange?: (agentId: string | null) => void;
	// `CreateNovaAgentCategoryMutation`/`UpdateNovaAgentMutation` have no `@connection`-backed way
	// to add a brand-new category or move an agent between categories into the Relay store (see
	// their comments) — the caller (NovaStudioPage) wires this to `ForecastQueryRenderer`'s own
	// `retry` so those cases fall back to a full catalogue refetch instead of a bespoke updater.
	onRefetchCatalogue?: () => void;
	// The selected agent's attached reference documents and the attach/remove actions — owned by
	// NovaStudioPage (fetched by the detail query, mutated + refetched there) and threaded through
	// only to the Prompt tab's Reference files section.
	referenceFiles: NovaReferenceFile[];
	onAttachFile: (file: File) => Promise<void>;
	onRemoveFile: (fileId: string) => Promise<void>;
	// The selected agent's deployment — owned by NovaStudioPage.tsx, fetched on demand (see
	// `onDeployTabOpened` below) rather than with the rest of the Studio, since only the Deploy tab
	// ever needs it. Threaded through only to the Deploy tab.
	deployment?: DeploymentSource | null;
	// The internal company/person directory that names the audience table's rows and feeds the audience
	// picker — owned by NovaStudioPage, fetched on demand (see `onOpenDirectory` below).
	directory?: DeployDirectory;
	isDirectoryLoading?: boolean;
	// Whether an audience mutation is currently in flight, so the Deploy tab can disable its own
	// controls while one is in progress.
	isDeploySubmitting?: boolean;
	isDeploymentLoading?: boolean;
	hasDeploymentReadFailed?: boolean;
	// The viewer's own company, as the schema's raw `backendId` — owned by NovaStudioPage.tsx (read off
	// the same deployment query as `deployment` above) and threaded through only so the Deploy tab can
	// encode it the way an audience entry's own id is encoded before comparing the two. Null until that
	// query has answered.
	viewerCompanyId?: number | null;
	// Adds or removes companies/people from the agent's audience — owned by NovaStudioPage.tsx.
	onAudienceSubmit?: (add: AudienceTargets, remove: AudienceTargets) => void;
	// Called whenever the Deploy tab needs company and person names — its table has rows to name, or a
	// picker opens — so the directory is fetched only when it is actually needed.
	onOpenDirectory?: () => void;
	// Called whenever the Deploy tab becomes the active tab for the selected agent, so the page can
	// re-read the deployment on demand rather than keeping it always fresh. Also the Deploy tab's retry
	// after a failed read.
	onDeployTabOpened?: (agentId: string) => void;
	// The selected workflow's declared variables, read with its detail. Empty for a task agent.
	workflowVariableGroups?: StudioWorkflowVariableGroup[];
	// The selected workflow's declaration could not be read, so its variables are unknown.
	workflowDeclarationFailed?: boolean;
	// The selected workflow's declaration has not been read yet.
	workflowDeclarationLoading?: boolean;
	// Reads the selected workflow's declaration again.
	onRetryWorkflowDeclaration?: () => void;
}

const EMPTY_DEPLOY_DIRECTORY: DeployDirectory = {companies: [], personsByCompanyId: new Map()};
const NO_WORKFLOWS: StudioWorkflowEntry[] = [];
const NO_WORKFLOW_VARIABLES: StudioWorkflowVariableGroup[] = [];

export function NovaStudioShell({
	categories,
	agents,
	workflows = NO_WORKFLOWS,
	draft,
	variables,
	removedVariables,
	models,
	defaultModelId,
	versions,
	onRestoreVersion,
	canSave = true,
	onSelectedAgentIdChange,
	onRefetchCatalogue,
	referenceFiles,
	onAttachFile,
	onRemoveFile,
	deployment = null,
	directory = EMPTY_DEPLOY_DIRECTORY,
	isDirectoryLoading = false,
	isDeploySubmitting = false,
	isDeploymentLoading = false,
	hasDeploymentReadFailed = false,
	viewerCompanyId = null,
	onAudienceSubmit = () => undefined,
	onOpenDirectory = () => undefined,
	onDeployTabOpened,
	workflowVariableGroups = NO_WORKFLOW_VARIABLES,
	workflowDeclarationFailed = false,
	workflowDeclarationLoading = false,
	onRetryWorkflowDeclaration = () => undefined,
}: Props): JSX.Element {
	const shell = useNovaStudioShell({categories, agents, workflows});
	const {formatMessage} = useIntl();

	useWarnOnUnsavedChanges(draft.isDirty);

	// Switching away from an agent with unsaved edits reloads the draft with the other agent's
	// content, silently discarding those edits. That is a pure in-app state change, so neither the
	// `beforeunload` prompt nor the router's `history.block` (both wired in useWarnOnUnsavedChanges)
	// can catch it — this confirm is the only guard on that path. Only guards an actual switch away
	// from a currently-selected agent (picking the first agent, or re-picking the current one, can't
	// lose edits). Reuses the same copy as the navigation guard.
	const handleSelectAgent = useCallback(
		(agentId: string): void => {
			const switchingAway = shell.selectedAgentId !== null && agentId !== shell.selectedAgentId;
			if (switchingAway && draft.isDirty && !window.confirm(formatMessage({id: 'common.save_changes_text'}))) {
				return;
			}
			shell.selectAgent(agentId);
		},
		[shell.selectedAgentId, shell.selectAgent, draft.isDirty, formatMessage]
	);

	useEffect(() => {
		onSelectedAgentIdChange?.(shell.selectedAgentId);
	}, [shell.selectedAgentId, onSelectedAgentIdChange]);

	// Fires once per genuine transition onto the Deploy tab for the currently selected agent — not on
	// every render. Selecting a different agent always lands back on the Prompt tab first (see
	// `selectAgent` in useNovaStudioShell), so `shell.activeTab` can only equal Deploy here for the
	// agent the deployment was fetched for; there is no case where `selectedAgentId` changes while
	// this condition is already true.
	useEffect(() => {
		if (shell.activeTab === StudioTab.Deploy && shell.selectedAgentId) {
			onDeployTabOpened?.(shell.selectedAgentId);
		}
	}, [shell.activeTab, shell.selectedAgentId, onDeployTabOpened]);

	// A model the service will refuse cannot be sent, and the draft sends whichever one it holds on
	// every save: the update input always carries `model`, and the service validates whatever the
	// version ends up with — the draft's value, or the current version's carried forward when none is
	// sent — so omitting it saves nothing. A save of an agent on a retired model is therefore rejected
	// even when only the prompts changed, and it surfaces as the generic mutation-error modal, which
	// never mentions the model. Blocking here instead costs the author nothing that was available:
	// the write was going to be refused either way, their edits stay in the draft, and the picker's
	// own warning names the model as the reason and offers the replacement that clears it.
	//
	// Only ever blocks on a *known* answer: `isNovaModelUnavailable` treats an empty list as "not
	// known" rather than "nothing is offered", so a registry read that failed leaves Save alone.
	//
	// An empty system prompt or user prompt template is refused by the service for the same reason —
	// no run can be built without either — so it blocks here too, with the editor's own "required"
	// issue naming which one.
	// A workflow's version name is required: the service refuses a workflow save without one.
	//
	// This is the one place the Studio asks which kind of agent is open: a task agent and a workflow
	// each get their own tabs, and the shared header, pill and version list read their wording from
	// the variant.
	const isWorkflow = shell.selectedAgent?.isWorkflow ?? false;
	const variant = isWorkflow ? WORKFLOW_VARIANT : TASK_AGENT_VARIANT;
	const canSaveDraft =
		canSave &&
		!isNovaModelUnavailable(models, draft.model) &&
		isRunnable(draft.promptState.values) &&
		(!variant.requiresVersionName || draft.versionName.trim() !== '');

	// Deploying serves the saved version, so the Deploy tab judges that rather than the draft: typing
	// prompts without saving them does not make the agent deployable. Only a *known* answer blocks —
	// until the selected agent's content has loaded (`canSave`), the saved values are placeholders.
	const isServedVersionRunnable = !canSave || isRunnable(draft.promptState.savedValues);

	// The guard (no-op while clean or a save is already in flight) lives in `isSaveDisabled` so it
	// covers both this handler's two triggers — the header button and the ⌘S/Ctrl+S shortcut below
	// — identically; nothing here decides on its own whether a save should happen. Depends on the
	// three `draft` fields actually read here, not `draft` itself, so the keydown listener effect
	// below doesn't tear down and re-attach on every render — `draft` is a fresh object every render
	// (see useNovaAgentDraft), which would defeat that effect's own dependency array otherwise.
	const handleSave = useCallback((): void => {
		if (isSaveDisabled(draft.isDirty, draft.saveState, canSaveDraft)) {
			return;
		}
		draft.save().catch(() => {
			// The draft's own save-state (Error) already reflects the failure and keeps the edits
			// around for retry — this only prevents an unhandled promise rejection.
		});
	}, [draft.isDirty, draft.saveState, draft.save, canSaveDraft]);

	useEffect(() => {
		function handleKeyDown(event: KeyboardEvent): void {
			const isSaveShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's';
			if (!isSaveShortcut) {
				return;
			}
			// Always swallow the browser's own Save dialog while the Studio is mounted, whether or
			// not there is anything to save right now.
			event.preventDefault();
			handleSave();
		}
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [handleSave]);

	const categoryOptions = useMemo(() => categories.map(({categoryId, name}) => ({categoryId, name})), [categories]);

	const handleCreateCategory = useCallback(
		(name: string): Promise<{categoryId: string; name: string}> =>
			new Promise((resolve, reject) => {
				Util.CommitMutation(
					CreateNovaAgentCategoryMutation,
					{name},
					response => {
						const category = response.createNovaAgentCategory?.category;
						if (!category) {
							reject(new Error('createNovaAgentCategory returned no category'));
							return;
						}
						// No `@connection` to insert into on this plain list (see the mutation's
						// own comment) — refetch so the new category shows up in the catalogue.
						onRefetchCatalogue?.();
						resolve({categoryId: Util.getUUIdFromBase64String(category.id), name: category.name ?? name});
					},
					true,
					null,
					(transaction, onFailure) => {
						// A taken name is a refusal the caller shows inline, not a failure: the
						// standard error modal would say only "something went wrong" and reload the
						// page on OK.
						if (transaction.toString().includes(`${CATEGORY_NAME_TAKEN}:`)) {
							reject(categoryNameTakenError());
							return;
						}
						// A true return means the fallback scheduled a retry of this same mutation
						// rather than surfacing an error, so this attempt isn't a final failure — the
						// retry's own success/failure settles this promise (see the create submit
						// below for the same guard).
						if (onFailure(transaction) === true) {
							return;
						}
						reject(new Error('createNovaAgentCategory mutation failed'));
					}
				);
			}),
		[onRefetchCatalogue]
	);

	const handleCreateSubmit = useCallback(
		(values: AgentFormValues): Promise<void> =>
			new Promise((resolve, reject) => {
				if (!values.categoryId) {
					reject(new Error('createNovaAgent requires a categoryId'));
					return;
				}
				const categoryId = values.categoryId;
				Util.CommitMutation(
					CreateNovaAgentMutation,
					{
						name: values.name,
						categoryId,
						title: values.title,
						description: values.description,
						model: values.model,
						categoryConnectionParentId: toRelayGlobalId('NovaAgentCategory', categoryId),
					},
					response => {
						const newAgentGlobalId = response.createNovaAgent?.agent?.node?.id;
						// The create mutation's RANGE_ADD already appended the edge, so the catalogue
						// updates itself; open the new agent (which resets to the Prompt tab).
						//
						// Runs at most once per submit. `closeModal` pops whatever sits on top of the
						// modal stack rather than closing a specific modal, so a second call would
						// dismiss an unrelated modal (e.g. the error modal from the follow-up update
						// below, or one the author opened since). It can genuinely be reached twice:
						// the follow-up update's failure handler opens the agent, and if
						// Util.CommitMutation retried that mutation behind the scenes, the retry's own
						// success/failure handler arrives afterwards and would open it again.
						let hasOpenedCreatedAgent = false;
						const openCreatedAgent = (): void => {
							if (hasOpenedCreatedAgent) {
								return;
							}
							hasOpenedCreatedAgent = true;
							closeModal();
							if (newAgentGlobalId) {
								shell.selectAgent(Util.getUUIdFromBase64String(newAgentGlobalId));
							}
							resolve();
						};
						// The create input carries no prompt bodies (see CreateNovaAgentInput), so if the
						// author typed any at create time, persist them as the agent's first authored
						// version with a follow-up update before opening it.
						const hasPromptContent = values.systemPrompt.trim() !== '' || values.userPromptTemplate.trim() !== '';
						if (!newAgentGlobalId || !hasPromptContent) {
							openCreatedAgent();
							return;
						}
						Util.CommitMutation(
							UpdateNovaAgentMutation,
							{
								id: newAgentGlobalId,
								name: values.name,
								categoryId,
								title: values.title,
								description: values.description,
								systemPrompt: values.systemPrompt,
								userPromptTemplate: values.userPromptTemplate,
								// agentMd is deliberately not sent: the update resolver preserves the
								// stored value for any prompt field left out, so omitting it can't
								// blank an agent.md the create path may seed now or later. The author
								// has no agent.md field at create time anyway.
							},
							() => openCreatedAgent(),
							true,
							null,
							(transaction, onFailure) => {
								// The agent was created; only saving its prompts failed. Open the agent
								// first — that closes this modal — and only then let the fallback surface
								// the standard error modal: `closeModal` pops the top of the modal stack,
								// so surfacing first would pop the error the author is meant to read and
								// leave the create modal standing. Opening it either way lets the author
								// re-save the prompts on the Prompt tab instead of creating a duplicate;
								// if the fallback retries the mutation instead of showing an error, the
								// retry's own handlers find the agent already opened (see
								// openCreatedAgent).
								openCreatedAgent();
								onFailure(transaction);
							}
						);
					},
					true,
					null,
					(transaction, onFailure) => {
						// Call the fallback so the standard error modal still fires, and reject
						// so the modal's own inline failure state renders too. The fallback returns
						// true when it has scheduled a retry of this same mutation instead of
						// surfacing an error (a transient network/CORS failure) — that is not a final
						// failure, so leave the submit in flight and let the retry's own success or
						// failure settle this promise, rather than flashing the modal's inline error
						// under a create that is about to succeed.
						if (onFailure(transaction) === true) {
							return;
						}
						reject(new Error('createNovaAgent mutation failed'));
					}
				);
			}),
		[shell]
	);

	// `shell.selectedAgent` (not `selectedAgentId`) is read here so the modal only ever opens for
	// an agent the catalogue still knows about — a since-deleted selection resolves to null and no-ops.
	const openHistoryModal = useCallback((): void => {
		const agent = shell.selectedAgent;
		if (!agent) {
			return;
		}
		showNovaVersionHistoryModal({
			versions,
			onRestore: onRestoreVersion,
			titleId: variant.versionListTitleId,
		});
	}, [shell.selectedAgent, versions, onRestoreVersion, variant]);

	// The delete mutation itself lives inside DeleteNovaCategoryModal (see showDeleteNovaCategoryModal.tsx) —
	// this only needs to trigger the catalogue refetch once it succeeds, mirroring how
	// handleCreateCategory reacts to CreateNovaAgentCategoryMutation above.
	const openDeleteCategoryModal = useCallback(
		(payload: DeleteCategoryPayload): void => {
			showDeleteNovaCategoryModal({
				...payload,
				onDeleted: () => onRefetchCatalogue?.(),
			});
		},
		[onRefetchCatalogue]
	);

	// Duplicating copies the *saved* record and then moves focus to the copy, so an author with
	// unsaved edits would silently lose them and land on a copy that doesn't match what was on screen.
	// Reuses the same guard and the same copy as handleSelectAgent, which protects the other path that
	// can discard a dirty draft.
	const handleDuplicateAgent = useCallback((): void => {
		const agent = shell.selectedAgent;
		if (!agent) {
			return;
		}
		if (draft.isDirty && !window.confirm(formatMessage({id: 'common.save_changes_text'}))) {
			return;
		}
		Util.CommitMutation(
			DuplicateNovaAgentMutation,
			{
				id: toRelayGlobalId('NovaAgent', agent.agentId),
				// The copy lands in the source's own category, so the connection to append the new edge
				// to is already known here and needs no round trip to discover.
				categoryConnectionParentId: toRelayGlobalId('NovaAgentCategory', agent.categoryId),
			},
			response => {
				const newAgentGlobalId = response.duplicateNovaAgent?.agent?.node?.id;
				if (newAgentGlobalId) {
					shell.selectAgent(Util.getUUIdFromBase64String(newAgentGlobalId));
				}
			}
		);
	}, [shell, draft.isDirty, formatMessage]);

	// The delete mutation itself lives inside DeleteNovaAgentModal — this only reacts to the outcome,
	// mirroring openDeleteCategoryModal above. The explicit selectAgent(null) matters: `selectedAgent`
	// resolves to null on its own once the agent leaves `agents`, but `selectedAgentId` would keep
	// holding the dead id, so onSelectedAgentIdChange would never fire and the page would strand the
	// deleted agent's detail state.
	const openDeleteAgentModal = useCallback((): void => {
		const agent = shell.selectedAgent;
		if (!agent) {
			return;
		}
		showDeleteNovaAgentModal({
			agentId: agent.agentId,
			agentName: studioAgentDisplayName(agent),
			onDeleted: () => {
				shell.selectAgent(null);
				onRefetchCatalogue?.();
			},
			// The reversible alternative: emptying the audience on the Deploy tab takes the agent out
			// of service without destroying it.
			onTurnOffInstead: () => shell.setActiveTab(StudioTab.Deploy),
		});
	}, [shell, onRefetchCatalogue]);

	const openCreateModal = useCallback((): void => {
		showCreateAgentModal({
			categories: categoryOptions,
			existingNames: agents.map(agent => agent.name),
			models,
			defaultModelId,
			onSubmit: handleCreateSubmit,
			onCreateCategory: handleCreateCategory,
		});
	}, [categoryOptions, agents, models, defaultModelId, handleCreateSubmit, handleCreateCategory]);

	// The open tab's content. A workflow stacks a failed-save message above it; a task agent shows it alone.
	const selectedAgent = shell.selectedAgent;
	const tabBody =
		selectedAgent === null ? null : (
			<>
				{shell.activeTab === StudioTab.Prompt && isWorkflow ? (
					<NovaWorkflowPromptTab
						draft={draft}
						variableGroups={workflowVariableGroups}
						declarationFailed={workflowDeclarationFailed}
						declarationLoading={workflowDeclarationLoading}
						onRetryDeclaration={onRetryWorkflowDeclaration}
						variables={variables}
						models={models}
						referenceFiles={referenceFiles}
						onAttachFile={onAttachFile}
						onRemoveFile={onRemoveFile}
					/>
				) : shell.activeTab === StudioTab.Prompt ? (
					<NovaPromptTab
						agentId={selectedAgent.agentId}
						agentName={studioAgentDisplayName(selectedAgent)}
						draft={draft}
						categoryOptions={categoryOptions}
						variables={variables}
						removed={removedVariables}
						models={models}
						onCreateCategory={handleCreateCategory}
						referenceFiles={referenceFiles}
						onAttachFile={onAttachFile}
						onRemoveFile={onRemoveFile}
					/>
				) : shell.activeTab === StudioTab.AgentMd && isWorkflow ? (
					<NovaWorkflowAgentMdTab
						draft={draft}
						variableGroups={workflowVariableGroups}
						declarationFailed={workflowDeclarationFailed}
						declarationLoading={workflowDeclarationLoading}
						onRetryDeclaration={onRetryWorkflowDeclaration}
						variables={variables}
					/>
				) : shell.activeTab === StudioTab.AgentMd ? (
					<NovaAgentMdTab draft={draft} variables={variables} removed={removedVariables} />
				) : shell.activeTab === StudioTab.Deploy ? (
					<NovaDeployTab
						agentName={studioAgentDisplayName(selectedAgent)}
						deployment={deployment}
						directory={directory}
						isDirectoryLoading={isDirectoryLoading}
						isSubmitting={isDeploySubmitting}
						isDeploymentLoading={isDeploymentLoading}
						hasDeploymentReadFailed={hasDeploymentReadFailed}
						viewerCompanyId={viewerCompanyId}
						isServedVersionRunnable={isServedVersionRunnable}
						onAudienceSubmit={onAudienceSubmit}
						onOpenDirectory={onOpenDirectory}
						onRetryDeploymentRead={() => {
							if (shell.selectedAgentId) {
								onDeployTabOpened?.(shell.selectedAgentId);
							}
						}}
					/>
				) : (
					// StudioTab may grow again before every case here is filled in — a string
					// beats a crash for whatever tab that turns out to be.
					`${formatMessage({id: TAB_LABEL_IDS[shell.activeTab]})} tab`
				)}
			</>
		);

	return (
		<Shell data-cy="nova-studio-shell">
			<NovaStudioCatalogue
				catalogue={shell.catalogue}
				workflows={workflows}
				libraryTab={shell.libraryTab}
				onLibraryTabChange={shell.setLibraryTab}
				selectedAgentId={shell.selectedAgentId}
				onSelectAgent={handleSelectAgent}
				onCreateAgent={openCreateModal}
				onDeleteCategory={openDeleteCategoryModal}
			/>
			<MainColumn>
				{shell.selectedAgent ? (
					<>
						<NovaStudioHeader
							agentName={studioAgentDisplayName(shell.selectedAgent)}
							saveState={draft.saveState}
							isDirty={draft.isDirty}
							canSave={canSaveDraft}
							onSave={handleSave}
							onOpenHistory={openHistoryModal}
							onDuplicateAgent={handleDuplicateAgent}
							onDeleteAgent={openDeleteAgentModal}
							variant={variant}
						/>
						<TabBar $padding={variant.tabBarPadding}>
							<Tabs value={shell.activeTab} onValueChange={tab => shell.setActiveTab(tab as StudioTab)}>
								<Tabs.List>
									{TAB_ORDER.map(tab => (
										<Tabs.Tab key={tab} value={tab} data-cy={`nova-tab-${tab}`}>
											{formatMessage({id: TAB_LABEL_IDS[tab]})}
										</Tabs.Tab>
									))}
								</Tabs.List>
							</Tabs>
						</TabBar>
						<TabContent $padding={variant.tabContentPadding} data-cy={`nova-tab-content-${shell.activeTab}`}>
							{variant.showsSaveErrorAlert ? (
								<ContentColumn>
									{draft.saveState === SaveState.Error && (
										<SaveErrorRow data-cy="nova-save-error">
											<SaveErrorMessage>
												<Alert
													type={Alert.TYPE.ERROR}
													text={formatMessage({id: 'nova.studio.saveError'})}
												/>
											</SaveErrorMessage>
											<Button emphasis="medium" data-cy="nova-save-retry" onPress={handleSave}>
												{formatMessage({id: 'nova.studio.saveRetry'})}
											</Button>
										</SaveErrorRow>
									)}
									{tabBody}
								</ContentColumn>
							) : (
								tabBody
							)}
						</TabContent>
					</>
				) : (
					<EmptyState data-cy="nova-studio-empty">{formatMessage({id: 'nova.studio.noAgentSelected'})}</EmptyState>
				)}
			</MainColumn>
		</Shell>
	);
}
